import React, { useState, useRef } from "react";
import { Formik } from "formik";
import { connect } from "react-redux";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import * as auth from "../../store/ducks/auth.duck";
import { login, me } from "../../crud/auth.crud";
import { toAbsoluteUrl } from "../../../_metronic";
import "../../styles/RedesignV2.scss";
import TextInputV2 from "../../components/redesign-v2/TextInputV2";
import { InputAdornment } from "@material-ui/core";
import ButtonV2 from "../../components/redesign-v2/ButtonV2";
import { Constants } from "../../utils/Constants";
import MfaVerify from "./MfaVerify";

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

function Login(props) {
  const [loading, setLoading] = useState(false);
  const [loadingButtonStyle, setLoadingButtonStyle] = useState({
    paddingRight: "2.5rem",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [mfaData, setMfaData] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const captchaRef = useRef(null);

  const enableLoading = () => {
    setLoading(true);
    setLoadingButtonStyle({ paddingRight: "3.5rem" });
  };

  const disableLoading = () => {
    setLoading(false);
    setLoadingButtonStyle({ paddingRight: "2.5rem" });
  };

  const validateLogin = (values) => {
    const errors = {};

    if (!values.email) {
      errors.email = "O e-mail é uma informação obrigatória.";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = "Você precisa informar um e-mail válido.";
    }

    if (!values.password) {
      errors.password = "A senha é uma informação obrigatória.";
    }

    return errors;
  };

  const validateAndStoreUser = async (authToken) => {
    const meResponse = await me();
    const meData = meResponse.data;

    if (meData.identity?.ctx !== "painel") {
      throw new Error("Contexto inválido para este painel.");
    }

    if (meData.session?.origin !== "web") {
      throw new Error("Origem inválida para este painel.");
    }

    props.login({ user: meData, authToken });
  };

  const performLogin = async (
    email,
    password,
    captchaToken,
    setStatus,
    setSubmitting
  ) => {
    try {
      const res = await login(email, password, captchaToken);

      if (res.data.mfa_required) {
        disableLoading();
        setMfaData(res.data);
        return;
      }

      await validateAndStoreUser(res.data.token);
      disableLoading();
    } catch (err) {
      disableLoading();
      setSubmitting(false);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Usuário ou senha incorretos.";
      setStatus(errorMessage);
    } finally {
      setPendingValues(null);
      if (captchaRef.current) captchaRef.current.resetCaptcha();
    }
  };

  const handleCaptchaVerify = (token) => {
    if (pendingValues) {
      performLogin(
        pendingValues.email,
        pendingValues.password,
        token,
        pendingValues.setStatus,
        pendingValues.setSubmitting
      );
    }
  };

  const handleCaptchaError = () => {
    disableLoading();
    if (pendingValues) {
      pendingValues.setSubmitting(false);
      pendingValues.setStatus(
        "Erro na verificação de segurança. Tente novamente."
      );
    }
    setPendingValues(null);
  };

  const onSubmitForm = async (values, setStatus, setSubmitting) => {
    enableLoading();
    setPendingValues({ ...values, setStatus, setSubmitting });

    if (HCAPTCHA_SITE_KEY && captchaRef.current) {
      captchaRef.current.execute();
    } else {
      performLogin(
        values.email,
        values.password,
        null,
        setStatus,
        setSubmitting
      );
    }
  };

  if (mfaData) {
    return (
      <MfaVerify
        mfaData={mfaData}
        onBack={() => setMfaData(null)}
        onSuccess={validateAndStoreUser}
      />
    );
  }

  return (
    <>
      <div className="card-login">
        <div className="card-login-container-logo">
          <img
            alt="Logo"
            src={toAbsoluteUrl("/images/new_logo.png")}
            style={{ height: "40px", marginBottom: "48px" }}
          />
        </div>

        <h3 className="card-login-title">Bem-vindo(a) ao Cashbarber!</h3>
        <h3 className="card-login-subtitle">
          Informe suas credenciais para acessar o sistema.
        </h3>

        <Formik
          initialValues={{ email: "", password: "" }}
          validate={(values) => validateLogin(values)}
          onSubmit={(values, { setStatus, setSubmitting }) =>
            onSubmitForm(values, setStatus, setSubmitting)
          }
        >
          {({
            values,
            status,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form
              noValidate={true}
              autoComplete="off"
              className="kt-form"
              onSubmit={handleSubmit}
              style={{
                marginTop: "48px",
                marginBottom: "0px",
                marginLeft: "0px",
                marginRight: "0px",
              }}
            >
              {status ? (
                <div role="alert" className="alert alert-danger">
                  <div className="alert-text">{status}</div>
                </div>
              ) : null}

              <div className="form-group">
                <TextInputV2
                  type="email"
                  placeholder="Informe o seu e-mail"
                  margin="normal"
                  className="kt-width-full"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  helperText={touched.email && errors.email}
                  error={Boolean(touched.email && errors.email)}
                  textLabel="E-mail"
                />
              </div>

              <div className="form-group">
                <TextInputV2
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  placeholder="Informe sua senha"
                  className="kt-width-full"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  helperText={touched.password && errors.password}
                  error={Boolean(touched.password && errors.password)}
                  textLabel="Senha"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ width: 40, height: 40 }}
                        position="end"
                        className="d-flex justify-content-center"
                      >
                        <i
                          className={
                            showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                          }
                          style={{ color: "#8C959B", cursor: "pointer" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className="card-login-container-links">
                <a className="forgot-password" href="/password/forgot">
                  Esqueceu sua senha?
                </a>
                {/* <a className="forgot-password" target='_blank' href={Constants.termosUsoURL}>Termos de uso</a>
								<a className="forgot-password" target='_blank' href={Constants.politicasPrivacidadeURL}>Políticas de privacidade</a> */}
              </div>

              <div className="card-login-container-button">
                <ButtonV2
                  id="kt_login_signin_submit"
                  type="submit"
                  disabled={loading}
                  className={`btn btn-v2-primary`}
                  style={loadingButtonStyle}
                  text={"Acessar"}
                  loading={loading}
                ></ButtonV2>
              </div>

              <div className="card-login-termos-politica">
                Ao continuar, você concorda com a{" "}
                <a
                  className="forgot-password"
                  target="_blank"
                  href={Constants.politicasPrivacidadeURL}
                >
                  Política de Privacidade
                </a>{" "}
                e os{" "}
                <a
                  className="forgot-password"
                  target="_blank"
                  href={Constants.termosUsoURL}
                >
                  Termos de uso
                </a>
              </div>

              {HCAPTCHA_SITE_KEY && (
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  size="invisible"
                  onVerify={handleCaptchaVerify}
                  onExpire={handleCaptchaError}
                  onError={handleCaptchaError}
                />
              )}
            </form>
          )}
        </Formik>
      </div>
    </>
  );
}

export default connect(null, auth.actions)(Login);
